import { AlertTriangle, Check, Loader2, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import type { StandReport } from "../../shared/types";
import { formatNif } from "../../shared/validation";

const cx = (...classes: Array<string | false | null | undefined>) => classes.filter(Boolean).join(" ");

export type ReportGenerationStep = {
  id: string;
  label: string;
  detail: string;
};

export const REPORT_GENERATION_STEP_WEIGHTS_MS = [3000, 4000, 4000, 5000, 6000, 3000] as const;

export const REPORT_GENERATION_STEPS: ReportGenerationStep[] = [
  { id: "identity", label: "Dados da empresa", detail: "VIES / NIF" },
  { id: "citius", label: "Insolvências e PER", detail: "CITIUS / Justiça.gov.pt" },
  { id: "debts", label: "Dívidas públicas", detail: "Finanças / Seg. Social" },
  { id: "processes", label: "Processos e publicações", detail: "Execuções, MJ, citações editais" },
  { id: "feedback", label: "Livro de Reclamações e menções web", detail: "Fontes públicas complementares" },
  { id: "score", label: "A calcular score de risco", detail: "Cruzamento final" },
];

const SLOW_MESSAGE_MS = 12_000;
const VERY_SLOW_MESSAGE_MS = 45_000;
const MAX_PROGRESS_PERCENT = 88;
const MAX_PROGRESS_VERY_SLOW_PERCENT = 92;

const TOTAL_SIMULATED_MS = REPORT_GENERATION_STEP_WEIGHTS_MS.reduce((sum, weight) => sum + weight, 0);

export type ReportProgressState = {
  activeStepIndex: number;
  completedCount: number;
  progressPercent: number;
  showSlowMessage: boolean;
  showVerySlowMessage: boolean;
  isQueued: boolean;
};

export const getWeightedProgressFraction = (elapsedMs: number) => {
  const elapsed = Math.max(0, elapsedMs);
  let accumulatedMs = 0;

  for (let index = 0; index < REPORT_GENERATION_STEP_WEIGHTS_MS.length; index += 1) {
    const weight = REPORT_GENERATION_STEP_WEIGHTS_MS[index];

    if (elapsed < accumulatedMs + weight) {
      const stepPartial = (elapsed - accumulatedMs) / weight;
      return (index + stepPartial) / REPORT_GENERATION_STEP_WEIGHTS_MS.length;
    }

    accumulatedMs += weight;
  }

  return (REPORT_GENERATION_STEP_WEIGHTS_MS.length - 0.12) / REPORT_GENERATION_STEP_WEIGHTS_MS.length;
};

export const getReportProgressState = (elapsedMs: number, status: StandReport["status"]): ReportProgressState => {
  if (status === "queued") {
    return {
      activeStepIndex: 0,
      completedCount: 0,
      progressPercent: 4,
      showSlowMessage: elapsedMs >= SLOW_MESSAGE_MS,
      showVerySlowMessage: elapsedMs >= VERY_SLOW_MESSAGE_MS,
      isQueued: true,
    };
  }

  let activeStepIndex = REPORT_GENERATION_STEPS.length - 1;
  let completedCount = 0;
  let accumulatedMs = 0;

  for (let index = 0; index < REPORT_GENERATION_STEP_WEIGHTS_MS.length; index += 1) {
    const weight = REPORT_GENERATION_STEP_WEIGHTS_MS[index];

    if (elapsedMs >= accumulatedMs + weight) {
      completedCount = index + 1;
      accumulatedMs += weight;
      continue;
    }

    activeStepIndex = index;
    break;
  }

  if (completedCount >= REPORT_GENERATION_STEPS.length) {
    activeStepIndex = REPORT_GENERATION_STEPS.length - 1;
    completedCount = REPORT_GENERATION_STEPS.length - 1;
  }

  const progressFraction = getWeightedProgressFraction(elapsedMs);
  let progressPercent = Math.min(progressFraction * MAX_PROGRESS_PERCENT, MAX_PROGRESS_PERCENT);

  if (elapsedMs >= VERY_SLOW_MESSAGE_MS) {
    const extra = Math.min((elapsedMs - VERY_SLOW_MESSAGE_MS) / 12_000, MAX_PROGRESS_VERY_SLOW_PERCENT - MAX_PROGRESS_PERCENT);
    progressPercent = Math.min(MAX_PROGRESS_PERCENT + extra, MAX_PROGRESS_VERY_SLOW_PERCENT);
  }

  return {
    activeStepIndex,
    completedCount,
    progressPercent,
    showSlowMessage: elapsedMs >= SLOW_MESSAGE_MS,
    showVerySlowMessage: elapsedMs >= VERY_SLOW_MESSAGE_MS,
    isQueued: false,
  };
};

export const formatElapsedLabel = (elapsedMs: number) => {
  const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1000));

  if (totalSeconds < 60) {
    return `há ${totalSeconds}s`;
  }

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return seconds > 0 ? `há ${minutes}m ${seconds}s` : `há ${minutes}m`;
};

const reportStatusCopy: Record<
  Exclude<StandReport["status"], "complete">,
  { title: string; body: string; failed?: boolean }
> = {
  queued: {
    title: "Relatório em fila",
    body: "O pedido foi recebido e vai começar assim que houver capacidade para consultar as fontes públicas.",
  },
  processing: {
    title: "A preparar o relatório completo",
    body: "Estamos a cruzar as fontes públicas. Esta página atualiza automaticamente quando o resultado estiver pronto.",
  },
  failed: {
    title: "Não foi possível gerar o relatório",
    body: "A consulta às fontes públicas falhou tecnicamente. Tente atualizar o relatório dentro de alguns minutos.",
    failed: true,
  },
};

type ReportPendingStateProps = {
  report: StandReport;
};

export const ReportPendingState = ({ report }: ReportPendingStateProps) => {
  const copy = reportStatusCopy[report.status === "complete" ? "processing" : report.status];
  const startedAtMs = new Date(report.checkedAt).getTime();
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (report.status === "failed") {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setNowMs(Date.now());
    }, 250);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [report.status]);

  const elapsedMs = Math.max(0, nowMs - startedAtMs);
  const progress = getReportProgressState(elapsedMs, report.status);
  const activeStep = REPORT_GENERATION_STEPS[progress.activeStepIndex];

  return (
    <div className="mx-auto max-w-3xl rounded-xl border border-border bg-card p-8 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3 text-foreground">
        <div className="flex items-center gap-3">
          {copy.failed ? (
            <AlertTriangle className="h-5 w-5 text-danger" />
          ) : (
            <RefreshCw className="h-5 w-5 animate-spin text-primary" />
          )}
          <p className="text-lg font-semibold">{copy.title}</p>
        </div>
        {!copy.failed && <p className="text-sm text-muted-foreground">{formatElapsedLabel(elapsedMs)}</p>}
      </div>

      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{copy.body}</p>

      {!copy.failed && (
        <div className="mt-6">
          <div className="flex items-center justify-between gap-3 text-xs font-medium text-muted-foreground">
            <span>Progresso estimado</span>
            <span>{Math.round(progress.progressPercent)}%</span>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-secondary">
            <div
              className="h-full rounded-full bg-primary transition-[width] duration-300 ease-out"
              style={{ width: `${progress.progressPercent}%` }}
            />
          </div>

          <ol className="mt-6 grid gap-2" aria-label="Etapas de geração do relatório">
            {REPORT_GENERATION_STEPS.map((step, index) => {
              const isCompleted = index < progress.completedCount;
              const isActive = !isCompleted && index === progress.activeStepIndex;
              const isQueuedStep = progress.isQueued && index === 0;

              return (
                <li
                  className={cx(
                    "flex items-start gap-3 rounded-lg border px-3 py-2.5 transition-colors",
                    isActive || isQueuedStep ? "border-primary/20 bg-primary/5" : "border-border bg-secondary/40",
                  )}
                  key={step.id}
                >
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center">
                    {isCompleted ? (
                      <Check className="h-4 w-4 text-success" aria-hidden />
                    ) : isActive || isQueuedStep ? (
                      <Loader2 className="h-4 w-4 animate-spin text-primary" aria-hidden />
                    ) : (
                      <span className="h-2 w-2 rounded-full bg-muted-foreground/40" aria-hidden />
                    )}
                  </span>
                  <div className="min-w-0">
                    <p className={cx("text-sm font-medium", isCompleted ? "text-muted-foreground" : "text-foreground")}>
                      {step.label}
                    </p>
                    {(isActive || isQueuedStep) && (
                      <p className="mt-0.5 text-xs text-muted-foreground" aria-live="polite">
                        {isQueuedStep ? "Em fila" : `A consultar ${step.detail}`}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>

          {progress.showSlowMessage && (
            <p className="mt-4 text-sm text-muted-foreground">
              {progress.showVerySlowMessage
                ? "Algumas fontes públicas estão a responder lentamente — não feche esta página."
                : "Algumas fontes públicas demoram mais — não feche esta página."}
            </p>
          )}

          {activeStep && !progress.isQueued && (
            <p className="sr-only" aria-live="polite">
              A consultar {activeStep.detail}
            </p>
          )}
        </div>
      )}

      <div className="mt-6 rounded-lg bg-secondary p-5">
        <p className="font-semibold text-foreground">{report.entity.name}</p>
        <p className="mt-1 font-mono text-sm text-muted-foreground">NIF {formatNif(report.entity.nif)}</p>
      </div>
    </div>
  );
};

export const TOTAL_REPORT_SIMULATED_MS = TOTAL_SIMULATED_MS;
